export interface ScaleRatio {
  name: string;
  value: number;
}

export const scaleRatios: ScaleRatio[] = [
  { name: 'Minor Second (1.067)', value: 1.067 },
  { name: 'Major Second (1.125)', value: 1.125 },
  { name: 'Minor Third (1.2)', value: 1.2 },
  { name: 'Major Third (1.25)', value: 1.25 },
  { name: 'Perfect Fourth (1.333)', value: 1.333 },
  { name: 'Augmented Fourth (1.414)', value: 1.414 },
  { name: 'Perfect Fifth (1.5)', value: 1.5 },
  { name: 'Golden Ratio (1.618)', value: 1.618 },
];

export interface TypeScale {
  name: string;
  minSize: number;
  maxSize: number;
}

export const scaleSteps = [
  { key: 'xs', label: 'Extra Small', steps: -3 },
  { key: 's', label: 'Small', steps: -2 },
  { key: 'm', label: 'Medium', steps: 0 },
  { key: 'l', label: 'Large', steps: 1 },
  { key: 'xl', label: 'Extra Large', steps: 2 },
  { key: '2xl', label: '2X Large', steps: 3 },
  { key: '3xl', label: '3X Large', steps: 4 },
  { key: '4xl', label: '4X Large', steps: 5 },
];

export function calculateTypeScale(
  minFontSize: number,
  maxFontSize: number,
  minRatio: number,
  maxRatio: number,
  baseIndex: string
): TypeScale[] {
  const baseStep = scaleSteps.find((step) => step.key === baseIndex) || scaleSteps[2];

  return scaleSteps.map((step) => {
    const stepsFromBase = step.steps - baseStep.steps;

    let minSize: number;
    let maxSize: number;

    if (stepsFromBase === 0) {
      minSize = minFontSize;
      maxSize = maxFontSize;
    } else if (stepsFromBase > 0) {
      minSize = minFontSize * Math.pow(minRatio, stepsFromBase);
      maxSize = maxFontSize * Math.pow(maxRatio, stepsFromBase);
    } else {
      // For scales below the base, use minRatio for both to maintain consistent scaling
      minSize = minFontSize / Math.pow(minRatio, Math.abs(stepsFromBase));
      maxSize = maxFontSize / Math.pow(minRatio, Math.abs(stepsFromBase));
    }

    // Apply minimum constraints
    minSize = Math.max(minSize, 9);
    maxSize = Math.max(maxSize, 11);

    // Ensure maxSize is always >= minSize
    if (maxSize < minSize) {
      maxSize = minSize;
    }

    return {
      name: step.key,
      minSize: Math.round(minSize),
      maxSize: Math.round(maxSize),
    };
  });
}

export function generateCSSVariables(
  scale: TypeScale[],
  namingConvention: string
): string {
  let css = ':root {\n';

  scale.forEach((item) => {
    css += `  --${namingConvention}-${item.name}: clamp(${item.minSize}px, ${calculateFluidValue(item.minSize, item.maxSize)}, ${item.maxSize}px);\n`;
  });

  css += '}';
  return css;
}

function calculateFluidValue(minSize: number, maxSize: number): string {
  const minViewport = 320;
  const maxViewport = 1200;

  const slope = (maxSize - minSize) / (maxViewport - minViewport);
  const yAxisIntersection = -minViewport * slope + minSize;

  return `${Math.round(yAxisIntersection)}px + ${(slope * 100).toFixed(2)}vw`;
}

export const popularFontFamilies = [
  { name: 'Inter', category: 'Sans-serif' },
  { name: 'Roboto', category: 'Sans-serif' },
  { name: 'Open Sans', category: 'Sans-serif' },
  { name: 'Lato', category: 'Sans-serif' },
  { name: 'Montserrat', category: 'Sans-serif' },
  { name: 'Poppins', category: 'Sans-serif' },
  { name: 'Raleway', category: 'Sans-serif' },
  { name: 'Source Sans Pro', category: 'Sans-serif' },
  { name: 'Nunito', category: 'Sans-serif' },
  { name: 'Playfair Display', category: 'Serif' },
  { name: 'Merriweather', category: 'Serif' },
  { name: 'Lora', category: 'Serif' },
  { name: 'PT Serif', category: 'Serif' },
  { name: 'Georgia', category: 'Serif' },
  { name: 'Crimson Text', category: 'Serif' },
  { name: 'Fira Code', category: 'Monospace' },
  { name: 'Source Code Pro', category: 'Monospace' },
  { name: 'JetBrains Mono', category: 'Monospace' },
  { name: 'IBM Plex Mono', category: 'Monospace' },
];
